import { useState, useCallback, useMemo, memo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { signOut } from 'reducers/userReducer';
import { isAdminUser } from 'utils/userUtils';
import { useOrganizations } from 'data/repositories/organizationsRepository';
import { shouldShowMultiOrganizationControls } from 'src/contexts/OrganizationContext';
import { showToast } from 'data/reducers/toastReducer';
import Dropdown from 'components/Dropdown';
import { ReactComponent as CircleUserIcon } from 'assets/icons/circle-user.svg';
import './AccountDropdown.css';

const AccountDropdown = memo(({ legacy }) => {
	const history = useHistory();
	const dispatch = useDispatch();
	const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);

	const { isLoggedIn, isAdmin } = useSelector((state) => ({
		isLoggedIn: !!state.user.userData?.id,
		isAdmin: isAdminUser(state.user.userData),
	}));

	const {
		items: organizations,
		active: activeOrganization,
		error: organizationsError,
		actions: orgActions,
	} = useOrganizations();

	// VWA-2752: switching only means something when there is somewhere else to
	// switch to. A user in exactly one organization sees the active-organization
	// summary and nothing else. The list is loaded once by OrganizationProvider
	// (src/contexts/OrganizationContext) at the app root; while that fetch is in
	// flight the list reads empty, which keeps the control hidden rather than
	// flashing it on. A list that FAILED to load is not a count of one, so the
	// shared rule keeps the control offered instead of latching it off for the
	// session.
	const belongsToMultipleOrganizations = shouldShowMultiOrganizationControls({
		items: organizations,
		error: organizationsError,
	});

	// Memoized initials generator
	const getInitials = useCallback((name) => {
		if (!name) return '??';
		const parts = name.split(' ').filter((p) => p.length > 0);
		if (parts.length >= 2) {
			return (parts[0][0] + parts[1][0]).toUpperCase();
		}
		return name.substring(0, 2).toUpperCase();
	}, []);

	// Handle organization switch
	const handleOrgSwitch = useCallback(
		async (orgId) => {
			if (orgId === activeOrganization?.id) {
				setShowOrgSwitcher(false);
				return;
			}

			try {
				await orgActions.switchOrganization({ id: orgId });
				localStorage.setItem('active_organization_id', orgId);
				sessionStorage.setItem('active_organization_id', orgId);

				dispatch(
					showToast({
						title: 'Success',
						body: 'Organization switched successfully',
						type: 'success',
					}),
				);

				// Refresh page to reload data
				window.location.reload();
			} catch (error) {
				dispatch(
					showToast({
						title: 'Error',
						body: 'Failed to switch organization',
						type: 'error',
					}),
				);
			}
		},
		[activeOrganization, orgActions, dispatch],
	);

	// Signed out menu items
	const signedOutMenuItems = useMemo(
		() => [
			{ text: 'Sign In', onClick: () => history.push('/sign-in') },
			{ text: 'Sign Up', onClick: () => history.push('/sign-up') },
		],
		[history],
	);

	// Build signed in menu items
	const signedInMenuItems = useMemo(() => {
		const items = [];

		// Dashboard menu
		items.push({
			text: 'Dashboard',
			onClick: () => history.push('/dashboard/overview'),
			icon: 'bi-clipboard-data',
		});

		// Admin menu
		if (isAdmin) {
			items.push({
				text: 'Admin',
				onClick: () => history.push('/admin/portfolios'),
				icon: 'bi-people',
			});
		}

		// User settings
		items.push({
			text: 'User Settings',
			onClick: () => history.push('/dashboard/settings'),
			icon: 'bi-gear',
		});

		// Sign out
		items.push({
			text: 'Sign Out',
			icon: 'bi-box-arrow-right',
			onClick: () => {
				dispatch(signOut());
				localStorage.removeItem('active_organization_id');
				sessionStorage.removeItem('active_organization_id');
				history.push('/sign-in');
			},
		});

		return items;
	}, [isAdmin, history, dispatch]);

	// Custom menu child for logged in users
	const renderSignedInMenu = useCallback(
		({ closeDropdown }) => {
			return (
				<div className="account-dropdown__menu">
					{/* Active Organization Section */}
					{activeOrganization && (
						<>
							<div className="account-dropdown__org-section">
								<div className="account-dropdown__org-active">
									<div className="account-dropdown__org-icon">
										{getInitials(activeOrganization.name)}
									</div>
									<div className="account-dropdown__org-details">
										<div className="account-dropdown__org-name">
											{activeOrganization.name}
										</div>
										<div className="account-dropdown__org-meta">
											{activeOrganization.user_role} •{' '}
											{activeOrganization.properties_count || 0} properties
										</div>
									</div>
									<span
										className={`badge ${
											activeOrganization.membership_type === 'owned'
												? 'bg-success'
												: 'bg-warning'
										}`}
										style={{ fontSize: '10px' }}
									>
										{activeOrganization.membership_type === 'owned'
											? 'Personal'
											: 'Shared'}
									</span>
								</div>

								{/* Switch Organization Toggle */}
								{belongsToMultipleOrganizations && (
									<>
										<button
											className="account-dropdown__org-switch-btn"
											onClick={() => setShowOrgSwitcher(!showOrgSwitcher)}
										>
											<i className="bi bi-arrow-repeat me-2"></i>
											{showOrgSwitcher
												? 'Hide Organizations'
												: 'Switch Organization'}
											<i
												className={`bi bi-chevron-${
													showOrgSwitcher ? 'up' : 'down'
												} ms-auto`}
											></i>
										</button>

										{/* Organization List (Collapsible) */}
										{showOrgSwitcher && (
											<div className="account-dropdown__org-list">
												{organizations.map((org) => (
													<div
														key={org.id}
														className={`account-dropdown__org-item ${
															org.id === activeOrganization.id
																? 'account-dropdown__org-item--active'
																: ''
														}`}
														onClick={() => {
															handleOrgSwitch(org.id);
															closeDropdown();
														}}
													>
														<div className="account-dropdown__org-item-icon">
															{getInitials(org.name)}
														</div>
														<div className="account-dropdown__org-item-details">
															<div className="account-dropdown__org-item-name">
																{org.name}
																{org.subscription && (
																	<span
																		className="badge bg-primary ms-2"
																		style={{ fontSize: '9px' }}
																	>
																		{org.subscription.plan_name || 'Free'}
																	</span>
																)}
															</div>
															<div className="account-dropdown__org-item-meta">
																{org.portfolio_sets_count || 0} portfolios •{' '}
																{org.properties_count || 0} properties
															</div>
														</div>
														{org.id === activeOrganization.id && (
															<i className="bi bi-check-circle-fill text-success"></i>
														)}
													</div>
												))}
											</div>
										)}
									</>
								)}

								{/* Manage Organizations Link */}
								{/* <button
									className="account-dropdown__org-manage-btn"
									onClick={() => {
										history.push('/dashboard/organizations');
										closeDropdown();
									}}
								>
									<i className="bi bi-gear me-2"></i>
									Manage Organizations
								</button> */}
							</div>

							<div className="account-dropdown__divider"></div>
						</>
					)}

					{/* Standard Menu Items */}
					{signedInMenuItems.map((item, index) => (
						<div key={index} className="vio-dropdown-menu-item">
							<button
								onClick={() => {
									item.onClick();
									closeDropdown();
								}}
							>
								{item.icon && (
									<span className="vio-dropdown-menu-item-icon">
										<i className={`bi ${item.icon}`} />
									</span>
								)}
								<span>{item.text}</span>
							</button>
						</div>
					))}
				</div>
			);
		},
		[
			activeOrganization,
			organizations,
			belongsToMultipleOrganizations,
			showOrgSwitcher,
			signedInMenuItems,
			getInitials,
			handleOrgSwitch,
		],
	);

	return (
		<div className="account-dropdown">
			<Dropdown
				buttonIcon={legacy ? 'bi-person-circle' : undefined}
				buttonIconNode={legacy ? undefined : <CircleUserIcon className="tw-h-4 tw-w-4" aria-hidden="true" />}
				hideChevron
				buttonClass={legacy ? '' : 'account-dropdown-v3-trigger'}
				menuItems={!isLoggedIn ? signedOutMenuItems : null}
				menuChild={isLoggedIn ? renderSignedInMenu : null}
			/>
		</div>
	);
});

AccountDropdown.displayName = 'AccountDropdown';

export default AccountDropdown;
